import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  Timestamp,
  increment
} from 'firebase/firestore';
import { db } from './firebase';


// Optimized Firestore collections structure for fast queries
const COLLECTIONS = {
  JOBS: 'jobs',
  APPLICATIONS: 'applications',
  COMPANIES: 'companies',
  JOB_STATS: 'jobStats'
};

// Job status constants
export const JOB_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  PAUSED: 'paused',
  CLOSED: 'closed',
  EXPIRED: 'expired'
};

const getJobOwnerIds = (jobData) => {
  return [jobData.userId, jobData.employerId, jobData.companyId].filter(Boolean);
};

const isEmployerJob = (jobData, employerId) => {
  return getJobOwnerIds(jobData).includes(employerId);
};

const getEmployerJobDocs = async (employerId) => {
  const ownerFields = ['userId', 'employerId', 'companyId'];
  const snapshots = await Promise.all(
    ownerFields.map(field =>
      getDocs(query(
        collection(db, COLLECTIONS.JOBS),
        where(field, '==', employerId)
      ))
    )
  );

  const docsById = new Map();
  snapshots.forEach(snapshot => {
    snapshot.docs.forEach(jobDoc => {
      docsById.set(jobDoc.id, jobDoc);
    });
  });

  return Array.from(docsById.values());
};

// Application status constants
export const APPLICATION_STATUS = {
  PENDING: 'pending',
  REVIEWED: 'reviewed',
  SHORTLISTED: 'shortlisted',
  INTERVIEWED: 'interviewed',
  REJECTED: 'rejected',
  HIRED: 'hired'
};

/**
 * Create a new job posting
 * @param {Object} jobData - Job posting data
 * @param {string} employerId - ID of the employer posting the job
 * @returns {Object} Result object with success status and job ID
 */
export const createJob = async (jobData, employerId) => {
  try {
    const now = Timestamp.now();

    // Prepare job document with optimized structure for queries
    const jobDoc = {
      // Basic Info
      title: jobData.title,
      company: jobData.company,
      userId: employerId,
      location: {
        city: jobData.location.city,
        state: jobData.location.state,
        country: jobData.location.country,
        remote: jobData.location.remote || false,
        hybrid: jobData.location.hybrid || false
      },

      // Job Details
      jobType: jobData.jobType, // full-time, part-time, contract, internship
      workMode: jobData.workMode, // remote, onsite, hybrid
      experienceLevel: jobData.experienceLevel, // entry, mid, senior, executive
      department: jobData.department || '',
      industry: jobData.industry || '', // IT, Electronics, Civil, etc.

      // Compensation
      salary: {
        min: jobData.salary?.min || null,
        max: jobData.salary?.max || null,
        currency: jobData.salary?.currency || 'USD',
        period: jobData.salary?.period || 'yearly', // yearly, monthly, hourly
        negotiable: jobData.salary?.negotiable || false
      },
      vacancies: jobData.vacancies || 1,

      // Content
      description: jobData.description,
      requirements: jobData.requirements || [],
      responsibilities: jobData.responsibilities || [],
      benefits: jobData.benefits || [],

      // Skills and Qualifications
      requiredSkills: jobData.requiredSkills || [],
      preferredSkills: jobData.preferredSkills || [],
      education: jobData.education || '',
      certifications: jobData.certifications || [],

      // Application Details
      applicationDeadline: jobData.applicationDeadline ? Timestamp.fromDate(new Date(jobData.applicationDeadline)) : null,
      applicationInstructions: jobData.applicationInstructions || '',

      // Status and Metadata
      status: jobData.status || JOB_STATUS.ACTIVE,
      featured: jobData.featured || false,
      urgent: jobData.urgent || false,

      // Timestamps
      createdAt: now,
      updatedAt: now,
      publishedAt: jobData.status === JOB_STATUS.ACTIVE ? now : null,

      // Stats (for fast dashboard queries)
      stats: {
        views: 0,
        applications: 0,
        shortlisted: 0,
        interviewed: 0,
        hired: 0
      },

      // Search optimization fields
      searchKeywords: [
        ...jobData.title.toLowerCase().split(' '),
        ...jobData.requiredSkills.map(skill => skill.toLowerCase()),
        jobData.location.city.toLowerCase(),
        jobData.jobType.toLowerCase(),
        jobData.experienceLevel.toLowerCase()
      ].filter(Boolean),

      // Indexing fields for efficient queries
      companyName_lower: jobData.company.toLowerCase(),
      title_lower: jobData.title.toLowerCase(),
      location_city_lower: jobData.location.city.toLowerCase()
    };

    // Add job to Firestore
    const docRef = await addDoc(collection(db, COLLECTIONS.JOBS), jobDoc);

    return {
      success: true,
      jobId: docRef.id,
      message: 'Job posted successfully!'
    };
  } catch (error) {
    console.error('Error creating job:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get jobs posted by a specific employer
 * @param {string} employerId - ID of the employer
 * @param {Object} options - Query options (status, limit, lastDoc)
 * @returns {Object} Result object with jobs array
 */
export const getEmployerJobs = async (employerId, options = {}) => {
  try {
    console.log('🔍 [getEmployerJobs] Starting query for employerId:', employerId);
    console.log('🔍 [getEmployerJobs] Options:', options);
    console.log('🔍 [getEmployerJobs] Collection name:', COLLECTIONS.JOBS);

    // Check if db is properly initialized
    if (!db) {
      console.error('❌ [getEmployerJobs] Firestore db is not initialized');
      return { success: false, error: 'Database not initialized', jobs: [] };
    }

    // Query jobs by userId only (without ordering to avoid index issues)
    const employerJobDocs = await getEmployerJobDocs(employerId);

    console.log('🔍 [getEmployerJobs] Basic query created');

    console.log('🔍 [getEmployerJobs] Executing Firestore query...');
    const querySnapshot = {
      docs: employerJobDocs,
      size: employerJobDocs.length,
      empty: employerJobDocs.length === 0,
      forEach: (callback) => employerJobDocs.forEach(callback)
    };
    console.log('✅ [getEmployerJobs] Query executed successfully');
    console.log('📊 [getEmployerJobs] Query snapshot size:', querySnapshot.size);
    console.log('📊 [getEmployerJobs] Query snapshot empty:', querySnapshot.empty);

    const jobs = [];
    const now = new Date();

    // Helper function to safely convert dates
    const convertDate = (dateValue) => {
      if (!dateValue) return null;
      if (typeof dateValue === 'number') return new Date(dateValue);
      if (typeof dateValue.toDate === 'function') return dateValue.toDate();
      if (dateValue instanceof Date) return dateValue;
      return null;
    };

    // Process jobs and check for expiration
    const updatePromises = [];

    querySnapshot.forEach((doc) => {
      console.log('📄 [getEmployerJobs] Processing document:', doc.id);
      const docData = doc.data();
      console.log('📄 [getEmployerJobs] Document data:', docData);

      const jobData = {
        id: doc.id,
        ...docData,
        vacancies: docData.vacancies || 1,
        createdAt: convertDate(docData.createdAt),
        updatedAt: convertDate(docData.updatedAt),
        publishedAt: convertDate(docData.publishedAt),
        applicationDeadline: convertDate(docData.applicationDeadline)
      };

      console.log('📄 [getEmployerJobs] Processed job data:', jobData);

      // Auto-expire jobs if deadline has passed and status is still active
      if (jobData.applicationDeadline &&
        jobData.applicationDeadline < now &&
        jobData.status === JOB_STATUS.ACTIVE) {
        console.log('⏰ [getEmployerJobs] Job has expired, updating status:', doc.id);
        jobData.status = JOB_STATUS.EXPIRED;

        // Update status in Firestore in the background
        const jobRef = doc.ref;
        updatePromises.push(
          updateDoc(jobRef, {
            status: JOB_STATUS.EXPIRED,
            updatedAt: Timestamp.now()
          }).catch(error => {
            console.error('❌ [getEmployerJobs] Error updating expired job:', error);
          })
        );
      }

      // Apply status filter in memory if specified
      if (!options.status || jobData.status === options.status) {
        jobs.push(jobData);
        console.log('✅ [getEmployerJobs] Job added to results');
      } else {
        console.log('❌ [getEmployerJobs] Job filtered out by status:', jobData.status, 'vs', options.status);
      }
    });

    // Wait for all status updates to complete (non-blocking)
    if (updatePromises.length > 0) {
      Promise.all(updatePromises).then(() => {
        console.log('✅ [getEmployerJobs] All expired job statuses updated');
      });
    }

    // Sort jobs by createdAt in memory (newest first)
    jobs.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.getTime() - a.createdAt.getTime();
    });

    // Apply limit in memory if specified
    const limitedJobs = options.limit ? jobs.slice(0, options.limit) : jobs;

    console.log('✅ [getEmployerJobs] Final processed jobs count:', limitedJobs.length);
    console.log('✅ [getEmployerJobs] Final jobs array:', limitedJobs);

    return {
      success: true,
      jobs: limitedJobs,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null
    };
  } catch (error) {
    console.error('❌ [getEmployerJobs] Error occurred:', error);
    console.error('❌ [getEmployerJobs] Error stack:', error.stack);
    return {
      success: false,
      error: error.message,
      jobs: []
    };
  }
};

/**
 * Get all active jobs for job seekers
 * @param {Object} filters - Filter options
 * @param {Object} pagination - Pagination options
 * @returns {Object} Result object with jobs array
 */
export const getActiveJobs = async (filters = {}, pagination = {}) => {
  try {
    console.log('🔍 [getActiveJobs] Starting query with filters:', filters);
    console.log('🔍 [getActiveJobs] Pagination:', pagination);
    console.log('🔍 [getActiveJobs] JOB_STATUS.ACTIVE:', JOB_STATUS.ACTIVE);
    console.log('🔍 [getActiveJobs] Collection name:', COLLECTIONS.JOBS);

    // Check if db is properly initialized
    if (!db) {
      console.error('❌ [getActiveJobs] Firestore db is not initialized');
      return { success: false, error: 'Database not initialized', jobs: [] };
    }

    // Start with basic query for active jobs (without ordering to avoid index issues)
    const q = query(
      collection(db, COLLECTIONS.JOBS),
      where('status', '==', JOB_STATUS.ACTIVE)
    );

    console.log('🔍 [getActiveJobs] Basic query created for active jobs');
    console.log('🔍 [getActiveJobs] Executing Firestore query...');
    const querySnapshot = await getDocs(q);
    console.log('✅ [getActiveJobs] Query executed successfully');
    console.log('📊 [getActiveJobs] Query snapshot size:', querySnapshot.size);
    console.log('📊 [getActiveJobs] Query snapshot empty:', querySnapshot.empty);

    const jobs = [];
    const now = new Date();

    querySnapshot.forEach((doc) => {
      console.log('📄 [getActiveJobs] Processing document:', doc.id);
      const docData = doc.data();
      console.log('📄 [getActiveJobs] Document data:', docData);
      console.log('📄 [getActiveJobs] Document status:', docData.status);

      const jobData = {
        id: doc.id,
        ...docData,
        createdAt: docData.createdAt?.toDate(),
        updatedAt: docData.updatedAt?.toDate(),
        publishedAt: docData.publishedAt?.toDate(),
        applicationDeadline: docData.applicationDeadline?.toDate()
      };

      console.log('📄 [getActiveJobs] Processed job data:', jobData);

      // Apply filters in memory to avoid complex compound queries
      let includeJob = true;
      let filterReasons = [];

      // Filter out expired jobs (applicationDeadline has passed)
      if (jobData.applicationDeadline && jobData.applicationDeadline < now) {
        includeJob = false;
        filterReasons.push(`expired: applicationDeadline ${jobData.applicationDeadline.toLocaleDateString()} has passed`);
      }

      if (filters.jobType && jobData.jobType !== filters.jobType) {
        includeJob = false;
        filterReasons.push(`jobType: ${jobData.jobType} !== ${filters.jobType}`);
      }

      if (filters.workMode && jobData.workMode !== filters.workMode) {
        includeJob = false;
        filterReasons.push(`workMode: ${jobData.workMode} !== ${filters.workMode}`);
      }

      if (filters.experienceLevel && jobData.experienceLevel !== filters.experienceLevel) {
        includeJob = false;
        filterReasons.push(`experienceLevel: ${jobData.experienceLevel} !== ${filters.experienceLevel}`);
      }

      if (filters.location && !jobData.location?.city?.toLowerCase().includes(filters.location.toLowerCase())) {
        includeJob = false;
        filterReasons.push(`location: ${jobData.location?.city} does not include ${filters.location}`);
      }

      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const titleMatch = jobData.title?.toLowerCase().includes(searchTerm);
        const companyMatch = jobData.company?.toLowerCase().includes(searchTerm);
        const locationMatch = jobData.location?.city?.toLowerCase().includes(searchTerm);
        const keywordsMatch = jobData.searchKeywords?.some(k => k.toLowerCase().includes(searchTerm));

        if (!titleMatch && !companyMatch && !locationMatch && !keywordsMatch) {
          includeJob = false;
          filterReasons.push(`search: "${filters.search}" not found in title, company, location, or keywords`);
        }
      }

      if (includeJob) {
        jobs.push(jobData);
        console.log('✅ [getActiveJobs] Job added to results');
      } else {
        console.log('❌ [getActiveJobs] Job filtered out:', filterReasons.join(', '));
      }
    });

    // Sort jobs by createdAt in memory (newest first)
    jobs.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.getTime() - a.createdAt.getTime();
    });

    // Apply limit in memory if specified
    const limitedJobs = pagination.limit ? jobs.slice(0, pagination.limit) : jobs;

    console.log('✅ [getActiveJobs] Final processed jobs count:', limitedJobs.length);
    console.log('✅ [getActiveJobs] Final jobs array:', limitedJobs);

    return {
      success: true,
      jobs: limitedJobs,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null
    };
  } catch (error) {
    console.error('❌ [getActiveJobs] Error occurred:', error);
    console.error('❌ [getActiveJobs] Error stack:', error.stack);
    return {
      success: false,
      error: error.message,
      jobs: []
    };
  }
};

/**
 * Update job status
 * @param {string} jobId - ID of the job to update
 * @param {string} status - New status
 * @param {string} employerId - ID of the employer (for authorization)
 * @returns {Object} Result object
 */
export const updateJobStatus = async (jobId, status, employerId) => {
  try {
    const jobRef = doc(db, COLLECTIONS.JOBS, jobId);
    const jobDoc = await getDoc(jobRef);

    if (!jobDoc.exists()) {
      return {
        success: false,
        error: 'Job not found'
      };
    }

    // Check if user owns this job
    const jobData = jobDoc.data();
    if (!isEmployerJob(jobData, employerId)) {
      return {
        success: false,
        error: 'Unauthorized to update this job'
      };
    }

    const updateData = {
      status,
      updatedAt: Timestamp.now()
    };

    // Set publishedAt when activating a job
    if (status === JOB_STATUS.ACTIVE && !jobDoc.data().publishedAt) {
      updateData.publishedAt = Timestamp.now();
    }

    await updateDoc(jobRef, updateData);

    return {
      success: true,
      message: 'Job status updated successfully'
    };
  } catch (error) {
    console.error('Error updating job status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update job details
 * @param {string} jobId - ID of the job to update
 * @param {Object} jobData - Updated job data
 * @param {string} employerId - ID of the employer (for authorization)
 * @returns {Object} Result object
 */
export const updateJob = async (jobId, jobData, employerId) => {
  try {
    console.log('🔧 [updateJob] Updating job:', jobId);
    console.log('🔧 [updateJob] Job data:', jobData);

    const jobRef = doc(db, COLLECTIONS.JOBS, jobId);
    const jobDoc = await getDoc(jobRef);

    if (!jobDoc.exists()) {
      return {
        success: false,
        error: 'Job not found'
      };
    }

    // Check if user owns this job
    const existingJobData = jobDoc.data();
    if (!isEmployerJob(existingJobData, employerId)) {
      return {
        success: false,
        error: 'Unauthorized to update this job'
      };
    }

    // Prepare update data with timestamps
    const updateData = {
      ...jobData,
      updatedAt: Timestamp.now(),
      vacancies: parseInt(jobData.vacancies, 10) || 1,
      // Convert applicationDeadline to Timestamp if provided
      applicationDeadline: jobData.applicationDeadline
        ? Timestamp.fromDate(new Date(jobData.applicationDeadline))
        : null
    };

    // Set publishedAt when activating a job for the first time
    if (jobData.status === JOB_STATUS.ACTIVE && !existingJobData.publishedAt) {
      updateData.publishedAt = Timestamp.now();
    }

    await updateDoc(jobRef, updateData);

    console.log('✅ [updateJob] Job updated successfully');
    return {
      success: true,
      message: 'Job updated successfully',
      jobId: jobId
    };
  } catch (error) {
    console.error('❌ [updateJob] Error updating job:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete a job posting
 * @param {string} jobId - ID of the job to delete
 * @param {string} employerId - ID of the employer (for authorization)
 * @returns {Object} Result object
 */
export const deleteJob = async (jobId, employerId) => {
  try {
    const jobRef = doc(db, COLLECTIONS.JOBS, jobId);
    const jobDoc = await getDoc(jobRef);

    if (!jobDoc.exists()) {
      return {
        success: false,
        error: 'Job not found'
      };
    }

    // Check if user owns this job
    const jobData = jobDoc.data();
    if (!isEmployerJob(jobData, employerId)) {
      return {
        success: false,
        error: 'Unauthorized to delete this job'
      };
    }

    await deleteDoc(jobRef);

    return {
      success: true,
      message: 'Job deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting job:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get job statistics for employer dashboard
 * @param {string} employerId - ID of the employer
 * @returns {Object} Result object with statistics
 */
export const getJobStats = async (employerId) => {
  try {
    console.log('🔍 [getJobStats] Getting stats for employerId:', employerId);

    const employerJobDocs = await getEmployerJobDocs(employerId);
    const jobs = [];

    employerJobDocs.forEach((jobDoc) => {
      jobs.push(jobDoc.data());
    });

    console.log('🔍 [getJobStats] Processing', jobs.length, 'jobs');

    const stats = {
      totalJobs: 0,
      activeJobs: 0,
      draftJobs: 0,
      closedJobs: 0,
      expiredJobs: 0,
      pausedJobs: 0,
      totalApplications: 0,
      totalViews: 0
    };

    jobs.forEach((job) => {
      stats.totalJobs++;

      switch (job.status) {
        case JOB_STATUS.ACTIVE:
          stats.activeJobs++;
          break;
        case JOB_STATUS.DRAFT:
          stats.draftJobs++;
          break;
        case JOB_STATUS.CLOSED:
          stats.closedJobs++;
          break;
        case JOB_STATUS.EXPIRED:
          stats.expiredJobs++;
          break;
        case JOB_STATUS.PAUSED:
          stats.pausedJobs++;
          break;
        default:
          break;
      }

      stats.totalApplications += job.stats?.applications || 0;
      stats.totalViews += job.stats?.views || 0;
    });

    console.log('🔍 [getJobStats] Final stats:', stats);

    return {
      success: true,
      stats
    };
  } catch (error) {
    console.error('Error fetching job stats:', error);
    return {
      success: false,
      error: error.message,
      stats: {
        totalJobs: 0,
        activeJobs: 0,
        draftJobs: 0,
        closedJobs: 0,
        expiredJobs: 0,
        pausedJobs: 0,
        totalApplications: 0,
        totalViews: 0
      }
    };
  }
};

/**
 * Increment job view count
 * @param {string} jobId - ID of the job
 * @returns {Object} Result object
 */
export const incrementJobViews = async (jobId) => {
  try {
    const jobRef = doc(db, COLLECTIONS.JOBS, jobId);
    await updateDoc(jobRef, {
      'stats.views': increment(1)
    });

    return { success: true };
  } catch (error) {
    console.error('Error incrementing job views:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single job by ID
 * @param {string} jobId - ID of the job
 * @returns {Object} Result object with job data
 */
export const getJobById = async (jobId) => {
  try {
    const jobRef = doc(db, COLLECTIONS.JOBS, jobId);
    const jobDoc = await getDoc(jobRef);

    if (!jobDoc.exists()) {
      return {
        success: false,
        error: 'Job not found'
      };
    }

    const jobData = {
      id: jobDoc.id,
      ...jobDoc.data(),
      createdAt: jobDoc.data().createdAt?.toDate(),
      updatedAt: jobDoc.data().updatedAt?.toDate(),
      publishedAt: jobDoc.data().publishedAt?.toDate(),
      applicationDeadline: jobDoc.data().applicationDeadline?.toDate()
    };

    return {
      success: true,
      job: jobData
    };
  } catch (error) {
    console.error('Error fetching job:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete a job posting (Super Admin - no authorization check)
 * @param {string} jobId - ID of the job to delete
 * @returns {Object} Result object
 */
export const deleteJobAdmin = async (jobId) => {
  try {
    const jobRef = doc(db, COLLECTIONS.JOBS, jobId);
    const jobDoc = await getDoc(jobRef);

    if (!jobDoc.exists()) {
      return {
        success: false,
        error: 'Job not found'
      };
    }

    await deleteDoc(jobRef);

    return {
      success: true,
      message: 'Job deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting job:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update job details (Super Admin - no authorization check)
 * @param {string} jobId - ID of the job to update
 * @param {Object} jobData - Updated job data
 * @returns {Object} Result object
 */
export const updateJobAdmin = async (jobId, jobData) => {
  try {
    console.log('🔧 [updateJobAdmin] Admin updating job:', jobId);
    const jobRef = doc(db, COLLECTIONS.JOBS, jobId);
    const jobDoc = await getDoc(jobRef);

    if (!jobDoc.exists()) {
      return {
        success: false,
        error: 'Job not found'
      };
    }

    const existingJobData = jobDoc.data();

    // Prepare update data with timestamps
    const updateData = {
      ...jobData,
      updatedAt: Timestamp.now(),
      vacancies: parseInt(jobData.vacancies, 10) || 1,
      applicationDeadline: jobData.applicationDeadline
        ? Timestamp.fromDate(new Date(jobData.applicationDeadline))
        : null
    };

    if (jobData.status === JOB_STATUS.ACTIVE && !existingJobData.publishedAt) {
      updateData.publishedAt = Timestamp.now();
    }

    await updateDoc(jobRef, updateData);

    return {
      success: true,
      message: 'Job updated successfully',
      jobId: jobId
    };
  } catch (error) {
    console.error('❌ [updateJobAdmin] Error updating job:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

